// The public composable. Components call `useUiSound()` and get a play()
// function plus reactive, writable preference state for a mute/volume UI. The
// engine (singleton) owns synthesis, gating, throttle, and persistence — this
// is a thin, reactive facade over it.

import { computed, ref } from 'vue'
import { config } from './config'
import { bindUnlock, onDidPlay, play, prefersReducedMotion } from './engine'
import type { PlayOptions, SoundCategory, SoundRole } from './types'

// Reactive mirror of `prefers-reduced-motion: reduce` so UI (mute icons,
// tooltips) can reflect the auto-mute live. The engine itself always queries
// the media state directly; this ref exists purely for reactivity.
const reducedMotionActive = ref(false)
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  try {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    reducedMotionActive.value = mq.matches
    mq.addEventListener?.('change', (e) => {
      reducedMotionActive.value = e.matches
    })
  } catch {
    /* matchMedia unavailable — treat as no-preference */
  }
}

/**
 * The single mutation path for the master enable. An explicit ENABLE performed
 * while `prefers-reduced-motion` is active is a knowing override and records
 * `prmOverride`; an explicit mute withdraws it. Enabling while PRM is off never
 * sets the override — if the user later turns on reduced motion, sounds
 * auto-mute again (that is the documented contract).
 */
function applyEnabled(value: boolean): void {
  config.value.enabled = value
  config.value.userSet = true
  if (value) {
    if (prefersReducedMotion()) {
      config.value.prmOverride = true
    }
  } else {
    config.value.prmOverride = false
  }
}

export interface UseUiSound {
  /** Play a sound by semantic role. Returns true if a sound was emitted. */
  play: (role: SoundRole, opts?: PlayOptions) => boolean
  /** Master on/off (writable). Setting it marks the preference user-explicit. */
  enabled: import('vue').WritableComputedRef<boolean>
  /** Master volume 0..0.8 (writable). */
  volume: import('vue').WritableComputedRef<number>
  /** True once any sound has actually played and the one-time hint is due. */
  hintShown: import('vue').ComputedRef<boolean>
  /**
   * True while `prefers-reduced-motion` is actively auto-muting (signal on and
   * the user has not knowingly overridden it). Mute UIs must reflect this —
   * `enabled` alone can read true while the engine is silent.
   */
  mutedByReducedMotion: import('vue').ComputedRef<boolean>
  /** Toggle master on/off in one call (marks user-explicit). */
  toggleMute: () => void
  /** Explicitly set master enable (marks user-explicit). */
  setEnabled: (value: boolean) => void
  /** Set master volume (clamped 0..0.8). */
  setVolume: (value: number) => void
  /** Read a per-category enable. */
  isCategoryEnabled: (category: SoundCategory) => boolean
  /** Set a per-category enable (marks user-explicit). */
  setCategory: (category: SoundCategory, value: boolean) => void
  /** Mark the one-time hint as shown (persisted). */
  markHintShown: () => void
  /** Subscribe to "a sound actually played"; returns an unsubscribe fn. */
  onDidPlay: (listener: () => void) => () => void
}

export function useUiSound(): UseUiSound {
  // Ensure gesture-unlock listeners exist the moment any surface uses sound.
  bindUnlock()

  const enabled = computed<boolean>({
    get: () => config.value.enabled,
    set: (value) => applyEnabled(value),
  })

  const volume = computed<number>({
    get: () => config.value.volume,
    set: (value) => {
      config.value.volume = Math.min(0.8, Math.max(0, value))
    },
  })

  const hintShown = computed<boolean>(() => config.value.hintShown)

  const mutedByReducedMotion = computed<boolean>(
    () => reducedMotionActive.value && !config.value.prmOverride,
  )

  return {
    play,
    enabled,
    volume,
    hintShown,
    mutedByReducedMotion,
    toggleMute: () => applyEnabled(!config.value.enabled),
    setEnabled: (value) => applyEnabled(value),
    setVolume: (value) => {
      config.value.volume = Math.min(0.8, Math.max(0, value))
    },
    isCategoryEnabled: (category) => config.value.categories[category],
    setCategory: (category, value) => {
      // Category tweaks are user-explicit metadata but NEVER a PRM override —
      // only an explicit master enable while PRM is active records that intent.
      config.value.categories[category] = value
      config.value.userSet = true
    },
    markHintShown: () => {
      config.value.hintShown = true
    },
    onDidPlay,
  }
}
